// Trending.js  

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  List,
  ListItem,
  Typography,
  Fade,
  CircularProgress
} from '@mui/material';
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs
} from 'firebase/firestore';
import { db } from '../utils/firebaseConfig';
import { getPostSummary } from '../utils/dbUtilities';
import { IoTrendingUpOutline } from 'react-icons/io5';
import VisibilityTracker from './VisibilityTracker';
import { useAuth } from '../contexts/AuthContext';
import './Trending.css';

const Trending = () => {
  const [trendingPosts, setTrendingPosts] = useState([]);
  const [clickedPosts, setClickedPosts] = useState(() => {
    const saved = localStorage.getItem('clickedPosts');
    return saved ? JSON.parse(saved) : {};
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  /**
   * Fetch the 20 most‑viewed Looksaway posts.
   *   • pulls ONLY from `looksPosts`
   *   • ignores deleted or reported posts
   *   • respects the same visibility switches used on Home‑feed /
   *     Discover box (`showOnHomePage` or `showInDiscoverBox`)
   */
  const fetchTrendingPosts = useCallback(async () => {
    setLoading(true);
    try {
      // 1️⃣ grab the 100 highest‑impression posts
      const postsRef = collection(db, 'looksPosts');
      const q        = query(postsRef, orderBy('impressionCount', 'desc'), limit(100));
      const snap     = await getDocs(q);

      // 2️⃣ weed out anything we never want to show
      const validPosts = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(p =>
          !p.deleted &&
          !p.isReported &&
          (p.showOnHomePage === true || p.showInDiscoverBox === true)
        );

      // 3️⃣ take the Top 20 after filtering
      const topPosts = validPosts.slice(0, 20);

      // 4️⃣ attach concise 8‑10‑word summaries (cached in `postSummaries`)
      const postsWithSummaries = await Promise.all(
        topPosts.map(async (post) => {
          const summary = await getPostSummary(post.id);
          return { ...post, summary };
        })
      );

      setTrendingPosts(postsWithSummaries);
    } catch (err) {
      console.error('[Looksaway Trending] error fetching posts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrendingPosts();
  }, [fetchTrendingPosts]);

  /** Keep clickedPosts in sync across tabs */
  useEffect(() => {
    const handleStorage = () => {
      const saved = localStorage.getItem('clickedPosts');
      if (saved && saved !== JSON.stringify(clickedPosts)) {
        setClickedPosts(JSON.parse(saved));
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [clickedPosts]);

  const handlePostClick = (postId) => {
    const updated = { ...clickedPosts, [postId]: true };
    setClickedPosts(updated);
    localStorage.setItem('clickedPosts', JSON.stringify(updated));
    navigate(`/looksposts/${postId}`);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 2,
        pt: { xs: 10, sm: 12, md: 15, lg: 8, xl: 8 }
      }}
      className="posts"
    >
      <Fade in timeout={800} unmountOnExit>
        <Paper
          elevation={3}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 4,
            mt: 4,
            width: '100%',
            maxWidth: '100%',
            mb: 2,
            borderRadius: '16px',
            backgroundColor: '#fff',
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <Box sx={{ width: '100%', mb: 2, textAlign: 'left' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IoTrendingUpOutline size={32} style={{ color: '#1d1d1f' }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1d1d1f', lineHeight: 1.2 }}>
                  Trending Posts
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: '#86868b',
                    fontWeight: 500,
                    fontSize: '1rem',
                    letterSpacing: '0.01em',
                    ml: '4px'
                  }}
                >
                  The People's Algorithm
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Body */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List sx={{ width: '100%', p: 0 }}>
              {trendingPosts.map((post, index) => (
                <VisibilityTracker
                  key={post.id}
                  postId={post.id}
                  isDiscoveryBox={false}
                  isPostAuthor={post.author === currentUser?.uid}
                >
                  <Fade in timeout={300} unmountOnExit>
                    <ListItem
                      component="div"
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        py: 2,
                        borderRadius: '8px',
                        '&:hover': { backgroundColor: 'transparent' }
                      }}
                    >
                      {/* Rank number */}
                      <Typography
                        variant="h4"
                        sx={{
                          color: '#86868b',
                          fontWeight: 700,
                          minWidth: 50,
                          mr: 2,
                          textAlign: 'center'
                        }}
                      >
                        {index + 1}
                      </Typography>

                      {/* Post details */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', mb: 1 }}>
                          <Typography
                            variant="body1"
                            component="span"
                            onClick={() => handlePostClick(post.id)}
                            sx={{
                              color: clickedPosts[post.id] ? '#551A8B' : '#1a0dab',
                              textDecoration: 'none',
                              cursor: 'pointer',
                              fontWeight: 'bold',
                              '&:hover': { textDecoration: 'underline' },
                              fontSize: '1.1rem',
                              mb: '4px'
                            }}
                          >
                            {post.title || post.userName || 'Untitled'}
                          </Typography>
                          <Typography variant="body2" component="span" sx={{ color: '#86868b', fontSize: '0.9rem' }}>
                            {post.city && post.state
                              ? `(${post.city}, ${post.state})`
                              : post.city
                              ? `(${post.city})`
                              : post.state
                              ? `(${post.state})`
                              : ''}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: '#1d1d1f', fontWeight: 400, fontSize: '0.95rem' }}>
                          {post.summary}
                        </Typography>
                      </Box>
                    </ListItem>
                  </Fade>
                </VisibilityTracker>
              ))}
            </List>
          )}
        </Paper>
      </Fade>
    </Box>
  );
};

export default Trending;

